'use client';

import cx from 'classnames';
import { useAtomValue } from 'jotai';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import ArrowRightIcon from '@flipster/ui/assets/svgs/arrow-right.svg';

import RelatedArticle from '../RelatedArticle';
import { relatedContentsAtom } from '../jotai';
import styles from './index.module.scss';

const RelatedContentsSection = ({ className }: { className?: string }) => {
  const relatedContents = useAtomValue(relatedContentsAtom);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLUListElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const updateButtons = () => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (!scrollContainerEl || !cardContainerEl) return;
    setHasPrev(scrollContainerEl.scrollLeft !== 0);
    setHasNext(
      scrollContainerEl.scrollLeft + cardContainerEl.offsetWidth + cardContainerEl.offsetLeft + 20 <
        cardContainerEl.scrollWidth,
    );
  };

  const handleScroll = useCallback(() => {
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(updateButtons, 200);
  }, []);

  useLayoutEffect(() => {
    setTimeout(() => handleScroll(), 100);
  }, [handleScroll]);

  const scrollToPrev = useCallback(() => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (scrollContainerEl && cardContainerEl) {
      const scrollTargetEl = Array.from(cardContainerEl.children)
        .reverse()
        .filter((element): element is HTMLElement => element.nodeType === Node.ELEMENT_NODE)
        .find((element) => element.offsetLeft - cardContainerEl.offsetLeft + 20 < scrollContainerEl.scrollLeft);
      if (!scrollTargetEl) return;
      scrollContainerEl.scrollTo({
        left: scrollTargetEl.offsetLeft - cardContainerEl.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollToNext = useCallback(() => {
    const scrollContainerEl = scrollContainerRef.current;
    const cardContainerEl = cardContainerRef.current;
    if (scrollContainerEl && cardContainerEl) {
      const scrollTargetEl = Array.from(cardContainerEl.children)
        .filter((element): element is HTMLElement => element.nodeType === Node.ELEMENT_NODE)
        .find((element) => element.offsetLeft - cardContainerEl.offsetLeft - 20 > scrollContainerEl.scrollLeft);
      if (!scrollTargetEl) return;
      scrollContainerEl.scrollTo({
        left: scrollTargetEl.offsetLeft - cardContainerEl.offsetLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <section className={cx(styles.section, className)}>
      <h2 className={styles.header}>Related content</h2>

      {relatedContents.length > 0 ? (
        <div className={styles.scrollContainer} ref={scrollContainerRef} onScroll={handleScroll}>
          <ul className={styles.cardContainer} ref={cardContainerRef}>
            {relatedContents.map((relatedGlossaryTerm) => (
              <li className={styles.card} key={relatedGlossaryTerm.slug}>
                <RelatedArticle article={relatedGlossaryTerm} />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className={styles.noRelatedContent}>There is no related content</p>
      )}

      <div className={styles.pagination}>
        <button type='button' className={styles.prevButton} onClick={scrollToPrev} disabled={!hasPrev} aria-hidden>
          <ArrowRightIcon />
        </button>
        <button type='button' className={styles.nextButton} onClick={scrollToNext} disabled={!hasNext} aria-hidden>
          <ArrowRightIcon />
        </button>
      </div>
    </section>
  );
};

export default RelatedContentsSection;
